/**
 * Pure helpers for the `pdf_survey` flow. No React, no DOM beyond
 * `atob`/`URL` so these can be exercised in plain Node tests.
 *
 * Spec: docs/onboarding/API/PDF-Resource-Type.md
 *
 * The candidate-facing flow:
 *   1. Server returns the schema with `document.{document_type_id, document_base64}`.
 *   2. Client decodes the base64 → template bytes.
 *   3. Candidate fills the embedded AcroForm fields (text + signature image).
 *   4. Client calls `bakeFilledPdf({...})` to write the values back into
 *      the AcroForm and embed the signature image, flatten, and serialize.
 *   5. Client uploads the resulting bytes via the document API.
 *
 * Field-name convention: each `columns[].items[].title` from the schema is
 * the literal PDF AcroForm field name. No separate `pdf_field_name` mapping.
 */

import type { PDFDocument } from 'pdf-lib';

export type SignatureFormat = 'image/png' | 'image/jpeg';

export interface SignatureInput {
  /** PDF AcroForm field name whose rect the signature image should fill. */
  fieldName: string;
  /** Data URL (`data:image/png;base64,...`) or raw `Uint8Array`. */
  data: string | Uint8Array;
  /** Defaults to PNG when omitted. */
  format?: SignatureFormat;
}

export interface BakeFilledPdfArgs {
  /** Raw template bytes (decoded from `document_base64`). */
  templateBytes: ArrayBuffer | Uint8Array;
  /**
   * Map of PDF AcroForm field name → string value. Keys that don't match a
   * text field in the template are silently skipped (so the same map can be
   * passed to different templates without coupling client to backend changes).
   */
  textValues?: Record<string, string>;
  /** Signatures to embed as images inside the named field's rect. */
  signatures?: SignatureInput[];
  /**
   * When true (default), the form is flattened so values become baked-in
   * page content. The upload step always wants flattened output; preview
   * rendering can pass `false` to keep the form editable.
   */
  flatten?: boolean;
}

/**
 * Decode a base64 string (with or without a `data:application/pdf;base64,`
 * prefix) into a `Uint8Array` of PDF bytes.
 */
export const decodeBase64Pdf = (base64: string): Uint8Array => {
  if (typeof base64 !== 'string' || base64.length === 0) {
    throw new Error('document_base64 is empty.');
  }
  const commaIdx = base64.indexOf(',');
  const payload = commaIdx >= 0 && base64.startsWith('data:') ? base64.slice(commaIdx + 1) : base64;
  const binary =
    typeof atob === 'function'
      ? atob(payload)
      : // Node fallback so this helper stays testable outside the browser.
        Buffer.from(payload, 'base64').toString('binary');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const dataUrlToBytes = (dataUrl: string): { bytes: Uint8Array; format: SignatureFormat } => {
  const m = /^data:(image\/(?:png|jpe?g));base64,(.*)$/i.exec(dataUrl);
  if (!m) throw new Error('Signature data URL is not a recognized image (png/jpeg).');
  const format = m[1].toLowerCase().includes('jpeg') ? 'image/jpeg' : 'image/png';
  const binary =
    typeof atob === 'function' ? atob(m[2]) : Buffer.from(m[2], 'base64').toString('binary');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { bytes, format };
};

const writeTextValuesToForm = (pdfDoc: PDFDocument, textValues: Record<string, string>): void => {
  const form = pdfDoc.getForm();
  const available = form.getFields().map((f) => f.getName());
  const availableSet = new Set(available);
  for (const [name, value] of Object.entries(textValues)) {
    if (value === undefined || value === null) continue;
    if (!availableSet.has(name)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[pdfBuilder] text field "${name}" not found in PDF template. Available AcroForm fields:`,
        available,
      );
      continue;
    }
    try {
      form.getTextField(name).setText(String(value));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[pdfBuilder] field "${name}" exists but is not a text field (or write failed):`,
        err,
      );
    }
  }
};

const embedSignatureIntoField = async (
  pdfDoc: PDFDocument,
  sig: SignatureInput,
): Promise<void> => {
  const form = pdfDoc.getForm();
  // Use `getField` (any type) instead of `getTextField` so the template can
  // declare the signature placeholder as a Text field, Signature field, etc.
  let field;
  try {
    field = form.getField(sig.fieldName);
  } catch {
    // Field absent — surface available field names so we can spot a naming
    // mismatch between the schema's form item title and the PDF AcroForm.
    const available = form.getFields().map((f) => f.getName());
    // eslint-disable-next-line no-console
    console.warn(
      `[pdfBuilder] signature field "${sig.fieldName}" not found in PDF template. Available AcroForm fields:`,
      available,
    );
    return;
  }
  const widgets = field.acroField.getWidgets();
  if (widgets.length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[pdfBuilder] signature field "${sig.fieldName}" exists but has no widget; cannot place image.`,
    );
    return;
  }
  const rect = widgets[0].getRectangle();

  let bytes: Uint8Array;
  let format: SignatureFormat;
  if (typeof sig.data === 'string') {
    const decoded = dataUrlToBytes(sig.data);
    bytes = decoded.bytes;
    format = decoded.format;
  } else {
    bytes = sig.data;
    format = sig.format ?? 'image/png';
  }

  const image = format === 'image/jpeg' ? await pdfDoc.embedJpg(bytes) : await pdfDoc.embedPng(bytes);
  const scale = Math.min(rect.width / image.width, rect.height / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  const x = rect.x + (rect.width - w) / 2;
  const y = rect.y + (rect.height - h) / 2;

  // Resolve the widget's page so the image lands on the correct one — the
  // AcroForm field may live anywhere in a multi-page template. Compare PDFRefs
  // by their object/generation numbers; pdf-lib does not always pool ref
  // instances, so `===` can return false for refs that point to the same
  // object and silently drop the image onto page 0.
  const pages = pdfDoc.getPages();
  const widgetPageRef = widgets[0].P();
  const page =
    pages.find((p) => {
      const r = p.ref;
      return (
        r === widgetPageRef ||
        (r &&
          widgetPageRef &&
          (r as any).objectNumber === (widgetPageRef as any).objectNumber &&
          (r as any).generationNumber === (widgetPageRef as any).generationNumber)
      );
    }) ?? pages[0];
  page.drawImage(image, { x, y, width: w, height: h });

  // Remove the field after drawing so the widget's empty appearance stream
  // (underline/box) no longer paints over the embedded image. Without this,
  // preview rendering (flatten=false) shows the signature widget on top of
  // the image and the signature appears missing. Wrapped in try/catch
  // because some field types in older templates are not removable.
  try {
    form.removeField(field);
  } catch {
    // Best-effort — flatten (when enabled) will collapse the widget anyway.
  }
};

/**
 * Build the final PDF bytes ready for upload. Imports `pdf-lib` dynamically
 * so this module's startup cost is zero until the first PDF survey is rendered.
 */
export const bakeFilledPdf = async ({
  templateBytes,
  textValues,
  signatures,
  flatten = true,
}: BakeFilledPdfArgs): Promise<Uint8Array> => {
  const { PDFDocument } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.load(templateBytes);

  if (textValues) writeTextValuesToForm(pdfDoc, textValues);
  if (signatures && signatures.length > 0) {
    for (const sig of signatures) {
      await embedSignatureIntoField(pdfDoc, sig);
    }
  }

  if (flatten) {
    pdfDoc.getForm().flatten();
  }

  return await pdfDoc.save();
};

/** Convenience: wrap baked bytes into a `File` for the upload endpoint. */
export const bytesToPdfFile = (bytes: Uint8Array, filename = 'signed.pdf'): File =>
  new File([new Uint8Array(bytes)], filename, { type: 'application/pdf' });

/**
 * Decide whether a form item should render as a signature pad. The doc's
 * example shows a `SingleLineText` widget named `candidate_signature`, so we
 * accept both:
 *   - explicit signal: `display_type: "signature"` (or `uidt: "Signature"`).
 *   - title-based heuristic: title contains "signature" (case-insensitive).
 *
 * If the metadata convention firms up, drop the heuristic.
 */
export const isSignatureField = (field: {
  title?: string;
  display_type?: string;
  uidt?: string;
}): boolean => {
  const dt = (field.display_type ?? '').toLowerCase();
  const uidt = (field.uidt ?? '').toLowerCase();
  if (dt === 'signature' || uidt === 'signature') return true;
  const title = (field.title ?? '').toLowerCase();
  return /signature/.test(title);
};
